import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Plus,
  Edit2,
  FileText,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Landmark,
  CalendarRange,
  UploadCloud,
} from 'lucide-react';
import { Card, Badge, Button, ConfirmationModal, toast } from '../../components/v2/V2Components';
import MortgageService from '../../services/MortgageService';
import BulkStatementImport from './BulkStatementImport';
import MortgageEscrowCard from './MortgageEscrowCard';
import StatementCoverageGrid from '../../components/v2/StatementCoverageGrid';
import { sortStatementsNewestFirst } from '../../utils/statementCoverage';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { mortgageMaturityDate, expectedNextDueDate } from '../../utils/dateHelpers';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const parseLocalDate = (d) => {
  if (!d) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (d) => {
  const date = parseLocalDate(d);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isImageFile = (filename = '') => /\.(png|jpg|jpeg|webp|gif)$/i.test(filename);

// `min-w-0` is load-bearing, not decoration: a grid item's automatic minimum
// size is clamped by its own min-width, so without it the `grid-cols-2` track
// can never shrink below the min-content width of an unbreakable currency
// string like "$278,856.00" and the card overflows its viewport on mobile.
// `break-words` gives those strings a last-resort break opportunity.
const Spec = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[var(--text-muted)] text-xs uppercase break-words">{label}</div>
    <div className="font-medium text-[var(--text-main)] break-words">{value ?? '—'}</div>
  </div>
);

const StatementRow = ({ statement, houseId, mortgageId, onDelete, navigate, hideActions = false }) => {
  const filename = statement.original_filename || statement.filename || '';
  const isImg = statement.url && isImageFile(filename);
  return (
    <tr className="border-b border-[var(--border)] last:border-0">
      <td className="py-3 px-2 text-sm text-[var(--text-main)]">{formatDate(statement.due_date)}</td>
      <td className="py-3 px-2 text-sm font-semibold text-[var(--text-main)]">
        {formatCurrency(statement.total_amount_due)}
      </td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)]">{formatCurrency(statement.principal_balance)}</td>
      <td className="py-3 px-2 text-sm">
        {statement.url ? (
          <a
            href={statement.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[var(--primary)] hover:underline"
          >
            {isImg ? <ImageIcon size={14} /> : <FileText size={14} />}
            <span className="truncate max-w-[120px]">{filename || 'View'}</span>
          </a>
        ) : (
          <span className="text-[var(--text-muted)]">—</span>
        )}
      </td>
      <td className="py-3 px-2 text-right whitespace-nowrap">
        {/* While the import panel is open, competing row actions are hidden:
            deleting a row mid-import would race the per-file merge. */}
        {!hideActions && (
          <>
            <button
              onClick={() => navigate(`/houses/${houseId}/mortgages/${mortgageId}/statements/${statement.id}/edit`)}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--primary)] hover:bg-[var(--bg-hover)]"
              aria-label="Edit statement"
              title="Edit statement"
            >
              <Edit2 size={16} />
            </button>
            <button
              onClick={() => onDelete(statement)}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-hover)]"
              aria-label="Delete statement"
              title="Delete statement"
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

const MortgageDetails = () => {
  usePageView(ANALYTICS_EVENTS.MORTGAGE_DETAILS_VIEWED);
  const { houseId, mortgageId } = useParams();
  const navigate = useNavigate();

  const [mortgage, setMortgage] = useState(null);
  const [statements, setStatements] = useState([]);
  const [disclosures, setDisclosures] = useState([]);
  const [disclosuresError, setDisclosuresError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [confirmDeleteMortgage, setConfirmDeleteMortgage] = useState(false);
  const [confirmDeleteStatement, setConfirmDeleteStatement] = useState(null);
  const [deletingMortgage, setDeletingMortgage] = useState(false);
  const [deletingStatement, setDeletingStatement] = useState(false);
  // null | 'import' — the inline bulk-import panel (BulkStatementImport).
  const [statementPanel, setStatementPanel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [m, statementsData, disclosuresData] = await Promise.all([
          MortgageService.getMortgage(mortgageId),
          MortgageService.getStatements(mortgageId),
          // Best-effort: a failed disclosure list must not take the loan and
          // its statements down with it. null marks the failure.
          MortgageService.getEscrowDisclosures(mortgageId).catch(() => null),
        ]);
        if (cancelled) return;
        // Guard against URL tampering: kick the user to the right house.
        if (houseId && String(m.house_id) !== String(houseId)) {
          navigate(`/houses/${m.house_id}/mortgages/${m.id}`, { replace: true });
          return;
        }
        setMortgage(m);
        setStatements(statementsData.statements || []);
        setDisclosures(disclosuresData?.disclosures || []);
        setDisclosuresError(disclosuresData ? null : "Couldn't load escrow disclosures. Refresh to try again.");
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err.code === 404) setLoadError('Mortgage not found.');
        else if (err.code === 401) setLoadError('Authentication required.');
        else setLoadError(err.message || 'Failed to load mortgage.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mortgageId, houseId, navigate]);

  const goBackToMortgages = () => navigate(`/houses/${houseId}?tab=mortgage`);

  const handleDeleteMortgage = async () => {
    try {
      setDeletingMortgage(true);
      await MortgageService.deleteMortgage(mortgageId);
      toast.success('Mortgage deleted');
      goBackToMortgages();
    } catch (err) {
      toast.error(err.message || 'Failed to delete mortgage');
      setDeletingMortgage(false);
    }
  };

  const refreshMortgage = async () => {
    // Balance, count and next due are derived from the latest statement.
    try {
      setMortgage(await MortgageService.getMortgage(mortgageId));
    } catch {
      /* non-fatal: stale summary is tolerable */
    }
  };

  // Bulk import merges each created statement as it lands, so the table and
  // the coverage grid fill in live and a mid-import stop leaves the page
  // matching the database. Dedupes on id because a fixed needs-attention row
  // arrives through this same path.
  const handleStatementsCreated = (newStatements) => {
    if (!newStatements?.length) return;
    setStatements((prev) => {
      const known = new Set(prev.map((s) => s.id));
      const fresh = newStatements.filter((s) => s && !known.has(s.id));
      return fresh.length ? sortStatementsNewestFirst([...fresh, ...prev]) : prev;
    });
  };

  const closeBulkImport = () => {
    setStatementPanel(null);
    refreshMortgage();
  };

  const handleDeleteStatement = async () => {
    if (!confirmDeleteStatement) return;
    try {
      setDeletingStatement(true);
      await MortgageService.deleteStatement(confirmDeleteStatement.id);
      setStatements((prev) => prev.filter((s) => s.id !== confirmDeleteStatement.id));
      setConfirmDeleteStatement(null);
      toast.success('Statement deleted');
      // Refresh the parent so the card summary (balance / count) stays accurate.
      await refreshMortgage();
    } catch (err) {
      toast.error(err.message || 'Failed to delete statement');
    } finally {
      setDeletingStatement(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card className="p-10 flex items-center justify-center text-[var(--text-muted)]">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading mortgage…
        </Card>
      </div>
    );
  }

  if (loadError || !mortgage) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Button leadingIcon={ArrowLeft} variant="ghost" onClick={goBackToMortgages}>
          Back
        </Button>
        <Card className="p-10 text-center mt-4">
          <AlertCircle size={32} className="mx-auto mb-3 text-[var(--error)]" />
          <p className="text-[var(--text-main)]">{loadError || 'Mortgage not found.'}</p>
        </Card>
      </div>
    );
  }

  const rate = mortgage.interest_rate != null ? `${mortgage.interest_rate}%` : '—';

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button
        onClick={goBackToMortgages}
        className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-main)] mb-4"
      >
        <ArrowLeft size={16} /> Back to mortgages
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-[var(--bg-hover)] text-[var(--primary)]">
          <Landmark size={28} />
        </div>
        <div>
          <h1 className="font-headline text-2xl font-bold text-[var(--text-main)]">{mortgage.lender}</h1>
          <div className="text-sm text-[var(--text-secondary)]">
            {mortgage.loan_type || 'Mortgage'}
            {mortgage.account_last4 && (
              <span className="text-[var(--text-muted)]"> · account ••••{mortgage.account_last4}</span>
            )}
          </div>
        </div>
      </div>

      {/* Loan facts */}
      <Card className="p-5 mb-6">
        {/* Stack at mobile, single row from sm up. Every Button carries
            whitespace-nowrap, so these three have a combined min-content width
            of ~310px against ~290px of card space on a 393px phone — the outer
            row needs to stack AND the button group needs to wrap, or the last
            child (Delete) is pushed off-screen. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between mb-4">
          <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">Loan details</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              leadingIcon={CalendarRange}
              size="sm"
              onClick={() => navigate(`/houses/${houseId}/mortgages/${mortgageId}/amortization`)}
            >
              Amortization
            </Button>
            <Button
              variant="ghost"
              leadingIcon={Edit2}
              size="sm"
              onClick={() => navigate(`/houses/${houseId}/mortgages/${mortgageId}/edit`)}
            >
              Edit
            </Button>
            <Button variant="destructive" leadingIcon={Trash2} size="sm" onClick={() => setConfirmDeleteMortgage(true)}>
              Delete
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <Spec label="Interest rate" value={rate} />
          <Spec label="Original amount" value={formatCurrency(mortgage.original_loan_amount)} />
          <Spec label="Current balance" value={formatCurrency(mortgage.current_principal_balance)} />
          <Spec label="Origination" value={formatDate(mortgage.origination_date)} />
          <Spec
            label="Maturity"
            value={formatDate(
              mortgage.maturity_date || mortgageMaturityDate(mortgage.origination_date, mortgage.term_months),
            )}
          />
          <Spec label="Term" value={mortgage.term_months ? `${mortgage.term_months} mo` : '—'} />
          <Spec label="Escrow" value={mortgage.has_escrow == null ? '—' : mortgage.has_escrow ? 'Yes' : 'No'} />
          <Spec label="Next due" value={formatDate(expectedNextDueDate(mortgage.next_due_date))} />
        </div>
        {mortgage.notes && <p className="text-sm text-[var(--text-secondary)] mt-4">{mortgage.notes}</p>}
      </Card>

      {/* Statements */}
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">Statements</h2>
            <Badge status={`${statements.length} ${statements.length === 1 ? 'statement' : 'statements'}`} />
          </div>
          {statementPanel === null && (
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                leadingIcon={Plus}
                className="flex-1 min-w-0 md:flex-none"
                onClick={() => navigate(`/houses/${houseId}/mortgages/${mortgageId}/statements/new`)}
              >
                Add Statement
              </Button>
              <Button
                variant="secondary"
                leadingIcon={UploadCloud}
                className="flex-1 min-w-0 md:flex-none"
                onClick={() => setStatementPanel('import')}
              >
                Bulk Statements
              </Button>
            </div>
          )}
        </div>

        {statementPanel === 'import' && (
          <BulkStatementImport
            mortgageId={mortgage.id}
            onStatementsCreated={handleStatementsCreated}
            onClose={closeBulkImport}
          />
        )}

        {statements.length > 0 && (
          <div className="mb-5">
            <StatementCoverageGrid key={mortgage.id} statements={statements} mortgage={mortgage} />
          </div>
        )}

        {statements.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            No statements yet. Add one to start a payment history.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table aria-label="Statements" className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase text-[var(--text-secondary)] border-b border-[var(--border)]">
                  <th className="py-2 px-2 font-semibold">Due</th>
                  <th className="py-2 px-2 font-semibold">Total Due</th>
                  <th className="py-2 px-2 font-semibold">Balance</th>
                  <th className="py-2 px-2 font-semibold">File</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {statements.map((s) => (
                  <StatementRow
                    key={s.id}
                    statement={s}
                    houseId={houseId}
                    mortgageId={mortgageId}
                    navigate={navigate}
                    onDelete={(st) => setConfirmDeleteStatement(st)}
                    hideActions={statementPanel !== null}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Escrow: shown for escrowed loans, for any loan that already has
          disclosures on file (has_escrow may have been left unset), and when
          the list failed to load, since there may be disclosures we can't see.
          The error stays up after an import: the list is still incomplete. */}
      {(mortgage.has_escrow !== false || disclosures.length > 0 || disclosuresError) && (
        <MortgageEscrowCard
          key={mortgage.id}
          mortgage={mortgage}
          disclosures={disclosures}
          loadError={disclosuresError}
          onDisclosuresChange={setDisclosures}
        />
      )}

      <ConfirmationModal
        isOpen={confirmDeleteMortgage}
        onClose={() => setConfirmDeleteMortgage(false)}
        onConfirm={handleDeleteMortgage}
        title="Delete Mortgage?"
        message={`This will permanently delete this mortgage, ${statements.length} ${statements.length === 1 ? 'statement' : 'statements'} and ${
          // Unknown when the list failed to load; never claim "0" before a permanent delete.
          disclosuresError
            ? 'any escrow disclosures'
            : `${disclosures.length} escrow ${disclosures.length === 1 ? 'disclosure' : 'disclosures'}`
        } (and any attached files). This cannot be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deletingMortgage}
      />

      <ConfirmationModal
        isOpen={!!confirmDeleteStatement}
        onClose={() => setConfirmDeleteStatement(null)}
        onConfirm={handleDeleteStatement}
        title="Delete Statement?"
        message="This will permanently delete the statement record and any attached file. This cannot be undone."
        confirmText="Delete"
        isDanger
        isLoading={deletingStatement}
      />
    </div>
  );
};

export default MortgageDetails;
